// src/views/Inventory/PengeluaranBarang/FormPengeluaranBarang.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  message,
  Modal,
} from "antd";
import {
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../../configs"; // sesuaikan path
import Title from "antd/es/typography/Title";

const { Text } = Typography;
const { Option } = Select;

// === KONSTAN ===
const NPWP = "063239834304400";

const DOC_OPTIONS = [
  { label: "BC 2.7", value: 27 },
  { label: "BC 2.8", value: 28 },
  { label: "BC 3.3", value: 33 },
  { label: "P3BET", value: 331 },
];

// generate SJ-YYMMDD001
const generateDeliveryNumber = (seq = 1) => {
  const today = dayjs().format("YYMMDD");
  return `SJ-${today}${String(seq).padStart(3, "0")}`;
};

export default function FormPengeluaranBarang() {
  const [form] = Form.useForm();

  const [nopenOptions, setNopenOptions] = useState([]);
  const [rawNopen, setRawNopen] = useState([]);
  const [loadingNopen, setLoadingNopen] = useState(false);
  const [loadingBarang, setLoadingBarang] = useState(false);
  const [saving, setSaving] = useState(false);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const [selectedMeta, setSelectedMeta] = useState(null);
  const [deliveryNumber, setDeliveryNumber] = useState(
    generateDeliveryNumber(),
  );

  // Store lotOptions separately from form state (keyed by kodeBarang)
  const [lotOptionsMap, setLotOptionsMap] = useState({});
  const itemsWatch = Form.useWatch("items", form);
  const totalBarangKeluar = useMemo(() => {
    if (!itemsWatch || !Array.isArray(itemsWatch)) return 0;
    return itemsWatch.reduce(
      (sum, row) => sum + (Number(row?.jumlahKeluar) || 0),
      0,
    );
  }, [itemsWatch]);

  // ==== AMBIL LIST NOPEN BERDASARKAN JENIS DOK ==== //
  const handleJenisDokumenChange = async (kodeDokumen) => {
    form.setFieldsValue({
      document_number: undefined,
      document_date: null,
      owner_name: undefined,
      container_number: undefined,
      items: [],
    });
    setSelectedMeta(null);
    setNopenOptions([]);
    setRawNopen([]);

    if (!kodeDokumen) return;
    let kodeRespon = "";
    switch (String(kodeDokumen)) {
      case "16":
        kodeRespon = "1603";
        break;
      case "27":
        kodeRespon = "2703";
        break;
      case "28":
        kodeRespon = "2803";
        break;
      case "33":
        kodeRespon = "3303";
        break;
      case "40":
        kodeRespon = "4003";
        break;
      case "331":
        kodeRespon = "33103";
        break;
      default:
        kodeRespon = "1603";
        break;
    }

    setLoadingNopen(true);
    try {
      const res = await axiosInstance.get("/portal/pasifik/get-data-nopen", {
        params: {
          kodeDokumen,
          // kodeRespon,
        },
      });

      // Support both wrapped and unwrapped responses (API returns array directly)
      const rawList = Array.isArray(res.data) ? res.data : res.data?.data || [];

      // Map snake_case to camelCase
      const list = rawList.map((item) => ({
        ...item,
        nomorDaftar: item.nomorDaftar || item.nomor_daftar,
        tanggalDaftar: item.tanggalDaftar || item.tanggal_daftar,
        nomorAju: item.nomorAju || item.nomor_aju,
        kodeValuta: item.kodeValuta || item.kode_valuta,
        cif: item.cif,
        entitas: item.nomor_identitas, // Check if entitas is joined or needs fetching
        kontainer: item.kontainer,
        kemasan: item.kemasan,
      }));

      setRawNopen(list);
      setNopenOptions(
        list.map((item) => ({
          label: `${item.nomorDaftar} - ${dayjs(item.tanggalDaftar).format("DD-MM-YYYY")}`,
          value: item.id, // Use unique header ID as value
        })),
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil daftar No Dokumen Pabean");
    } finally {
      setLoadingNopen(false);
    }
  };

  // ==== AMBIL DETAIL NOPEN + BARANG ==== //
  const handleNopenChange = async (headerId) => {
    if (!headerId) {
      setSelectedMeta(null);
      form.setFieldsValue({
        document_date: null,
        owner_name: undefined,
        container_number: undefined,
        items: [],
      });
      return;
    }

    // Find document by unique ID
    const header = rawNopen.find((d) => d.id === headerId);
    if (!header) {
      message.error("Detail dokumen tidak ditemukan");
      return;
    }

    const entitas = header.entitas || [];
    const vendorEntitas = entitas[1] || null;
    const ownerEntitas = header.nomor_identitas || null;

    const nomorIdentitasVendor = vendorEntitas?.nomorIdentitas || null;

    // const ownerName = ownerEntitas || "";
    const kontainer = (header.kontainer || []).map((k) => k.nomorKontainer);
    const containerNumber = kontainer.join(",");

    const tanggalDaftar = header.tanggalDaftar || null;
    const kodeValuta = header.kodeValuta || null;
    const cif = header.cif || 0;

    const kemasanArr = header.kemasan || [];
    const kemasanInfo = kemasanArr[0] || {};
    const headerTotalKemasan = kemasanInfo.jumlahKemasan || 0;
    const merkKemasan = kemasanInfo.merkKemasan || null;

    const nomorDaftar = header.nomorDaftar;

    setSelectedMeta({
      headerId, // Store unique header ID
      nomorDaftar,
      tanggalDaftar,
      kodeValuta,
      cif,
      nomorAju: header.nomorAju,
      kodeNegaraAsal: header.kodeNegaraAsal || null,
      containerNumber,
      id_contact: ownerEntitas,
      id_vendor: nomorIdentitasVendor,
      headerTotalKemasan,
      merkKemasan,
    });

    // Generate SJ number based on Nopen
    const generatedSJ = `SJ-${dayjs().format("YYYYMMDD")}-${nomorDaftar}`;

    form.setFieldsValue({
      delivery_number: generatedSJ,
      delivery_date: dayjs(),
      document_number: nomorDaftar,
      document_date: tanggalDaftar ? dayjs(tanggalDaftar) : null,
      owner_name: ownerEntitas
        .replace(/[^0-9]/g, "")
        .padStart(16, "0")
        .slice(0, 16),
      container_number: containerNumber || undefined,
    });

    await fetchBarang(headerId, nomorDaftar, { kodeValuta, cif });
  };

  // ==== AMBIL BARANG + EXPAND PER LOT DARI /api/item-stock ==== //
  const fetchBarang = async (headerId, nomorDaftar, metaFromHeader = {}) => {
    setLoadingBarang(true);
    try {
      const res = await axiosInstance.get(
        "/portal/pasifik/get-data-barang-by-header-id",
        {
          params: {
            headerId,
          },
        },
      );

      // Support both wrapped and unwrapped responses
      const rawList = Array.isArray(res.data) ? res.data : res.data?.data || [];

      if (!rawList.length) {
        form.setFieldsValue({ items: [] });
        message.warning("Tidak ada data barang untuk dokumen ini");
        return;
      }

      // Transform snake_case to camelCase (matching incoming form)
      const list = rawList.map((r) => ({
        ...r,
        seriBarang: r.seri_barang || r.seriBarang,
        kodeBarang: r.kode_barang || r.kodeBarang,
        jumlahKemasan: r.jumlah_kemasan || r.jumlahKemasan,
        kodeJenisKemasan: r.kode_kemasan || r.kodeJenisKemasan || r.kodeKemasan,
        jumlahSatuan: r.jumlah_satuan || r.jumlahSatuan,
        kodeSatuanBarang: r.kode_satuan || r.kodeSatuanBarang || r.kodeSatuan,
        cifRupiah: r.cif_rupiah || r.cifRupiah,
        merk: r.merek || r.merk,
        uraian: r.uraian || r.uraianBarang,
      }));

      const allRows = [];
      const newLotOptionsMap = {}; // Build LOT options map for each item

      for (const b of list) {
        // Get nopen_asal and nomor_aju_asal from API barang (td_barang)
        // These may be null if the item doesn't have origin info
        const apiNopenAsal = b.nomor_daftar_asal || b.nomorDaftarAsal || null;
        const apiNomorAjuAsal = b.nomor_aju_asal || b.nomorAjuAsal || null;
        const apiTanggalNopenAsal =
          b.tanggal_daftar_asal || b.tanggalDaftarAsal || null;

        const baseMeta = {
          namaBarang: `${b.uraian} (${b.kodeBarang})`,
          nopenAsal: apiNopenAsal, // From API, user can edit if null
          nomorAjuAsal: apiNomorAjuAsal,
          tanggalNopenAsal: apiTanggalNopenAsal,
          seri: b.seriBarang,
          // default values
          stokSaatIni: 0,
          stokSatuan: 0,
          lot: "",
          jumlahKeluar: b.jumlahKemasan,
          // satuan fields
          kodeSatuanBarang: b.kodeSatuanBarang,
          jumlahSatuan: b.jumlahSatuan,
          // hidden meta
          kodeBarang: b.kodeBarang,
          merk: b.merk,
          kodeJenisKemasan: b.kodeJenisKemasan,
          cif: b.cif ?? metaFromHeader.cif ?? 0,
          idItemStock: null,
        };

        try {
          const stockRes = await axiosInstance.get("/item-stock", {
            params: { item_code: b.kodeBarang, nopen: apiNopenAsal },
          });
          const stockList = stockRes.data?.data || [];

          if (stockList.length === 0) {
            // No stock found, just use baseMeta
            allRows.push({
              ...baseMeta,
              stokSaatIni: 0,
              stokSatuan: 0,
              lot: "",
              idItemStock: null,
            });
          } else {
            // Aggregate total stock for this item
            const totalKemasan = stockList.reduce(
              (sum, s) => sum + (s.jumlah_kemasan || 0),
              0,
            );
            const totalSatuan = stockList.reduce(
              (sum, s) => sum + (s.jumlah_satuan || 0),
              0,
            );

            // Check if all LOTs are the same
            const uniqueLots = [
              ...new Set(stockList.map((s) => s.lokasi_lot).filter(Boolean)),
            ];
            const sameLot = uniqueLots.length === 1 ? uniqueLots[0] : "";

            // Store lot options in separate state (keyed by kodeBarang)
            const lotOptions = stockList.map((s) => ({
              value: s.lokasi_lot,
              label: `${s.lokasi_lot} (Stok: ${s.jumlah_kemasan})`,
              id_item_stock: s.id_item_stock,
              jumlah_kemasan: s.jumlah_kemasan,
            }));

            // Build map entry for this item
            newLotOptionsMap[b.kodeBarang] = lotOptions;

            allRows.push({
              ...baseMeta,
              stokSaatIni: totalKemasan,
              stokSatuan: totalSatuan,
              lot: sameLot, // Fill if all same, otherwise empty for user to pick
              idItemStock:
                stockList.length === 1 ? stockList[0].id_item_stock : null,
            });
          }
        } catch (e) {
          console.error("Gagal ambil item-stock untuk", b.kodeBarang, e);
          allRows.push({ ...baseMeta });
        }
      }

      // Update lotOptionsMap state
      setLotOptionsMap(newLotOptionsMap);
      form.setFieldsValue({ items: allRows });
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil data barang");
    } finally {
      setLoadingBarang(false);
    }
  };

  // ==== SUBMIT KE BE OUTGOING (payload baru + update stok item-stock) ==== //
  const handleSubmit = async (values) => {
    if (!selectedMeta) {
      message.error("Silakan pilih Jenis & No Dokumen Pabean terlebih dahulu");
      return;
    }

    const items = values.items || [];
    if (!items.length) {
      message.error("Tidak ada barang yang akan dikeluarkan");
      return;
    }

    // VALIDASI: jumlahKeluar per baris tidak boleh > stokSaatIni
    const invalid = items.find(
      (row) =>
        row.lot !== "LS.99" &&
        Number(row.jumlahKeluar || 0) > Number(row.stokSaatIni || 0) &&
        Number(row.stokSaatIni || 0) > 0,
    );
    if (invalid) {
      message.error(
        "Jumlah keluar tidak boleh lebih besar dari stok saat ini per LOT",
      );
      return;
    }

    // Filter items yang jumlahKeluar > 0
    const validItems = items.filter(
      (row) => (Number(row.jumlahKeluar) || 0) > 0,
    );

    if (validItems.length === 0) {
      message.error("Tidak ada barang yang akan dikeluarkan (jumlah > 0)");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;
    const items = values.items || [];

    // Re-filter valid items (should be same as before)
    const validItems = items.filter(
      (row) => (Number(row.jumlahKeluar) || 0) > 0,
    );

    const { nomorDaftar, tanggalDaftar, kodeValuta, cif, id_contact } =
      selectedMeta;

    const headerPayload = {
      id_outgoing: 0,
      delivery_number: values.delivery_number,
      delivery_date: values.delivery_date
        ? dayjs(values.delivery_date).format("YYYY-MM-DD")
        : null,
      id_customer: id_contact
        ? String(id_contact)
            .replace(/[^0-9]/g, "")
            .padStart(16, "0")
            .slice(1, 16)
        : "-",
      document_number: nomorDaftar,
      document_date: tanggalDaftar
        ? dayjs(tanggalDaftar).format("YYYY-MM-DD")
        : null,
      id_document: values.id_document,
      id_cif_currency: kodeValuta,
      total_packaging: totalBarangKeluar,
      cif: cif,
      created_by: 0,
    };

    const detailsPayload = validItems.map((row) => ({
      id_outgoing_detail: 0,
      id_outgoing: 0,
      id_customer: id_contact
        ? String(id_contact)
            .replace(/[^0-9]/g, "")
            .padStart(16, "0")
            .slice(1, 16)
        : "-",
      nopen_asal: row.nopenAsal || nomorDaftar,
      tanggal_nopen_asal: row.tanggalNopenAsal
        ? dayjs(row.tanggalNopenAsal).format("YYYY-MM-DD")
        : tanggalDaftar
          ? dayjs(tanggalDaftar).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
      nomor_aju_asal: row.nomorAjuAsal || selectedMeta?.nomorAju || null,
      seri: row.seri,
      item_code: row.kodeBarang,
      kemasan: row.kodeJenisKemasan,
      total_kemasan: Number(row.jumlahKeluar) || 0,
      satuan: row.kodeSatuanBarang || null,
      total_satuan: Number(row.jumlahSatuan) || 0,
      lot: row.lot || "",
      created_by: 0,
      updated_by: 0,
    }));

    const stockUpdates = items
      .filter((row) => row.idItemStock && row.stokSaatIni != null)
      .map((row) => {
        const stok = Number(row.stokSaatIni) || 0;
        const keluar = Number(row.jumlahKeluar) || 0;
        return {
          id_item_stock: row.idItemStock,
          new_jumlah_kemasan: stok - keluar,
        };
      });

    try {
      setSaving(true);

      // 1) Simpan outgoing
      await axiosInstance.post("/outgoing", {
        header: headerPayload,
        details: detailsPayload,
      });

      message.success("Data pengeluaran & stok gudang berhasil disimpan");

      setDeliveryNumber(generateDeliveryNumber(2));
      form.resetFields();
      form.setFieldsValue({
        delivery_number: generateDeliveryNumber(),
        delivery_date: dayjs(),
      });
      setSelectedMeta(null);
      setNopenOptions([]);
      setRawNopen([]);
      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error("Gagal menyimpan pengeluaran barang / update stok");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={3} style={{ marginBottom: 4 }}>
            Form Pengeluaran Barang
          </Title>
          <Text type="secondary">
            Kelola daftar Pengeluaran Barang untuk kebutuhan inventory &
            kepabeanan.
          </Text>
        </Col>
      </Row>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          items: [
            {
              jumlahKeluar: 0,
            },
          ],
        }}
      >
        {/* BARIS 1: No SJ & Tanggal */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Surat Jalan</Text>}
              name="delivery_number"
              rules={[
                { required: true, message: "No Surat Jalan wajib diisi" },
              ]}
            >
              <Input placeholder="No Surat Jalan" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Surat Jalan</Text>}
              name="delivery_date"
              rules={[
                { required: true, message: "Tanggal Surat Jalan wajib diisi" },
              ]}
            >
              <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS 2: Jenis Dok & No Dok */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Jenis Dokumen Pabean</Text>}
              name="id_document"
              rules={[
                { required: true, message: "Jenis dokumen wajib dipilih" },
              ]}
            >
              <Select
                placeholder="Jenis Dokumen Pabean"
                options={DOC_OPTIONS}
                onChange={handleJenisDokumenChange}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Dokumen Pabean</Text>}
              name="document_number"
              rules={[{ required: true, message: "No dokumen wajib dipilih" }]}
            >
              <Select
                placeholder="Pilih No Dokumen Pabean"
                loading={loadingNopen}
                showSearch
                optionFilterProp="label"
                onChange={handleNopenChange}
              >
                {nopenOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS 3: Tanggal Dok + Pemilik */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>Tanggal Dokumen Pabean</Text>}
              name="document_date"
            >
              <DatePicker
                disabled
                style={{ width: "100%" }}
                format="DD MMMM YYYY"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label={<Text strong>Pemilik</Text>} name="owner_name">
              <Input disabled placeholder="Pemilik" />
            </Form.Item>
          </Col>
        </Row>

        {/* BARIS 4: No Container + No Plat */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Kontainer</Text>}
              name="container_number"
              extra="Pisahkan dengan koma jika lebih dari satu"
            >
              <Input placeholder="No Kontainer" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={<Text strong>No Plat Kendaraan</Text>}
              name="plat_number"
            >
              <Input placeholder="No Plat Kendaraan" />
            </Form.Item>
          </Col>
        </Row>

        {/* LAMPIRAN */}
        <Row style={{ marginTop: 8, marginBottom: 16 }}>
          <Col span={24}>
            <Text strong>Lampiran 1</Text>
          </Col>
          <Col span={24} style={{ marginTop: 8 }}>
            <Space direction="vertical" size={4}>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                style={{
                  background: "#FFA800",
                  borderColor: "#FFA800",
                  fontWeight: 600,
                }}
                onClick={() =>
                  document.getElementById("upload-lampiran-outgoing").click()
                }
              >
                Upload File
              </Button>
              <input
                id="upload-lampiran-outgoing"
                type="file"
                style={{ display: "none" }}
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    message.info(
                      `File "${file.name}" siap diupload (integrasikan dengan payload header.lampiran_1 jika diperlukan).`,
                    );
                  }
                }}
              />
              <Text type="secondary">
                Jenis File: JPG | JPEG | PNG | GIF | PDF | DOC | DOCX | XLS |
                XLSX. Maks 5 MB.
              </Text>
            </Space>
          </Col>
        </Row>

        {/* TABEL BARANG: NAMA, STOK, NOPEN, SERI, JUMLAH KELUAR, LOT */}
        <Card
          bodyStyle={{ padding: 0 }}
          style={{
            marginTop: 16,
            borderRadius: 12,
            border: "1px solid #E4E6EF",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "2.5fr 0.6fr 1fr 0.5fr 0.8fr 0.6fr 0.8fr 0.6fr 0.8fr 50px",
              background: "#F3F6F9",
              padding: "8px 16px",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <div>NAMA BARANG *</div>
            <div>STOK</div>
            <div>NOPEN ASAL</div>
            <div>SERI</div>
            <div>JML KELUAR</div>
            <div>KEMASAN</div>
            <div>JML SATUAN</div>
            <div>SATUAN</div>
            <div>LOT</div>
            <div style={{ textAlign: "center" }}>AKSI</div>
          </div>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => {
                  const itemsVal = form.getFieldValue("items") || [];
                  const row = itemsVal[field.name] || {};

                  return (
                    <div key={field.key}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "2.5fr 0.6fr 1fr 0.5fr 0.8fr 0.6fr 0.8fr 0.6fr 0.8fr 50px",
                          padding: "8px 16px",
                          borderTop: "1px solid #E4E6EF",
                          alignItems: "center",
                        }}
                      >
                        {/* Nama Barang */}
                        <Form.Item
                          {...field}
                          name={[field.name, "namaBarang"]}
                          fieldKey={[field.fieldKey, "namaBarang"]}
                          rules={[
                            {
                              required: true,
                              message: "Nama barang wajib diisi",
                            },
                          ]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="Nama Barang" />
                        </Form.Item>

                        {/* Stok Saat Ini (dari item-stock) */}
                        <Form.Item
                          {...field}
                          name={[field.name, "stokSaatIni"]}
                          fieldKey={[field.fieldKey, "stokSaatIni"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="0" disabled />
                        </Form.Item>

                        {/* Nopen Asal */}
                        <Form.Item
                          {...field}
                          name={[field.name, "nopenAsal"]}
                          fieldKey={[field.fieldKey, "nopenAsal"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="Nopen" />
                        </Form.Item>

                        {/* Seri */}
                        <Form.Item
                          {...field}
                          name={[field.name, "seri"]}
                          fieldKey={[field.fieldKey, "seri"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="Seri" />
                        </Form.Item>

                        {/* Jumlah Keluar */}
                        <Form.Item
                          {...field}
                          name={[field.name, "jumlahKeluar"]}
                          fieldKey={[field.fieldKey, "jumlahKeluar"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                          rules={[
                            {
                              required: true,
                              message: "Wajib",
                            },
                          ]}
                        >
                          <Input placeholder="0" type="number" min={0} />
                        </Form.Item>

                        {/* Kemasan (kode jenis kemasan) */}
                        <Form.Item
                          {...field}
                          name={[field.name, "kodeJenisKemasan"]}
                          fieldKey={[field.fieldKey, "kodeJenisKemasan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="Kms" />
                        </Form.Item>

                        {/* Jumlah Satuan */}
                        <Form.Item
                          {...field}
                          name={[field.name, "jumlahSatuan"]}
                          fieldKey={[field.fieldKey, "jumlahSatuan"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="0" type="number" min={0} />
                        </Form.Item>

                        {/* Satuan (kode satuan) */}
                        <Form.Item
                          {...field}
                          name={[field.name, "kodeSatuanBarang"]}
                          fieldKey={[field.fieldKey, "kodeSatuanBarang"]}
                          style={{ marginBottom: 0, paddingRight: 8 }}
                        >
                          <Input placeholder="Sat" />
                        </Form.Item>

                        {/* LOT - Show Select if multiple options, otherwise Input */}
                        {(() => {
                          const itemLotOptions =
                            lotOptionsMap[row.kodeBarang] || [];
                          if (itemLotOptions.length > 1) {
                            return (
                              <div style={{ marginBottom: 0, paddingRight: 8 }}>
                                <Select
                                  placeholder="Pilih LOT"
                                  style={{ width: "100%" }}
                                  value={row.lot || undefined}
                                  onChange={(val) => {
                                    // Update form state directly
                                    const selectedOpt = itemLotOptions.find(
                                      (o) => o.value === val,
                                    );
                                    const items =
                                      form.getFieldValue("items") || [];
                                    if (items[field.name]) {
                                      items[field.name].lot = val;
                                      if (selectedOpt) {
                                        items[field.name].idItemStock =
                                          selectedOpt.id_item_stock;
                                      }
                                      form.setFieldsValue({
                                        items: [...items],
                                      });
                                    }
                                  }}
                                >
                                  {itemLotOptions.map((opt, idx) => (
                                    <Select.Option key={idx} value={opt.value}>
                                      {opt.label}
                                    </Select.Option>
                                  ))}
                                </Select>
                              </div>
                            );
                          }
                          return (
                            <Form.Item
                              {...field}
                              name={[field.name, "lot"]}
                              fieldKey={[field.fieldKey, "lot"]}
                              style={{ marginBottom: 0, paddingRight: 8 }}
                              rules={[{ required: true, message: "Lot wajib" }]}
                            >
                              <Input placeholder="Rak.Lantai.Kolom" />
                            </Form.Item>
                          );
                        })()}

                        <div style={{ textAlign: "center" }}>
                          <Button
                            type="text"
                            icon={
                              <DeleteOutlined style={{ color: "#F64E60" }} />
                            }
                            onClick={() => remove(field.name)}
                          />
                        </div>

                        {/* hidden meta utk payload & update stok */}
                        <Form.Item
                          name={[field.name, "kodeBarang"]}
                          fieldKey={[field.fieldKey, "kodeBarang"]}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "merk"]}
                          fieldKey={[field.fieldKey, "merk"]}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "kodeSatuanBarang"]}
                          fieldKey={[field.fieldKey, "kodeSatuanBarang"]}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "jumlahSatuan"]}
                          fieldKey={[field.fieldKey, "jumlahSatuan"]}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "kodeJenisKemasan"]}
                          fieldKey={[field.fieldKey, "kodeJenisKemasan"]}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "cif"]}
                          fieldKey={[field.fieldKey, "cif"]}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "idItemStock"]}
                          fieldKey={[field.fieldKey, "idItemStock"]}
                          hidden
                        >
                          <Input />
                        </Form.Item>
                      </div>
                    </div>
                  );
                })}

                <div style={{ padding: "8px 16px 12px" }}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      add({
                        namaBarang: "",
                        stokSaatIni: 0,
                        nopenAsal: "",
                        seri: "",
                        jumlahKeluar: 0,
                        lot: "",
                        kodeBarang: "",
                        kodeJenisKemasan: "",
                        idItemStock: null,
                      })
                    }
                  >
                    Tambah Baris
                  </Button>
                </div>
              </>
            )}
          </Form.List>
        </Card>

        {/* TOTAL BARANG KELUAR */}
        <div
          style={{
            marginTop: 12,
            textAlign: "left",
            fontWeight: 600,
          }}
        >
          TOTAL BARANG KELUAR: {totalBarangKeluar}
        </div>

        {/* TOMBOL AKSI */}
        <Row justify="end" style={{ marginTop: 24 }}>
          <Space>
            <Button danger> Batal </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={saving || loadingBarang}
              style={{
                minWidth: 120,
                fontWeight: 600,
              }}
            >
              Simpan Pengeluaran
            </Button>
          </Space>
        </Row>
      </Form>

      <Modal
        title="Konfirmasi Pengeluaran Barang"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        <p>Apakah Anda yakin ingin menyimpan data pengeluaran barang ini?</p>
        <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4 }}>
          <p style={{ margin: 0 }}>
            <strong>No Surat Jalan:</strong> {pendingValues?.delivery_number}
          </p>
          <p style={{ margin: 0 }}>
            <strong>Tanggal:</strong>{" "}
            {pendingValues?.delivery_date
              ? dayjs(pendingValues.delivery_date).format("DD MMMM YYYY")
              : "-"}
          </p>
          <p style={{ margin: 0 }}>
            <strong>No Dokumen:</strong> {selectedMeta?.nomorDaftar}
          </p>
        </div>

        <div style={{ marginTop: 16 }}>
          <Text strong>Detail Barang Keluar:</Text>
          <div
            style={{
              maxHeight: 200,
              overflowY: "auto",
              marginTop: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <table
              style={{
                width: "100%",
                fontSize: 12,
                borderCollapse: "collapse",
              }}
            >
              <thead
                style={{ background: "#fafafa", position: "sticky", top: 0 }}
              >
                <tr>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "left",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Nama Barang
                  </th>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "right",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Jml Keluar
                  </th>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "left",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Lot
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingValues?.items
                  ?.filter((i) => (Number(i.jumlahKeluar) || 0) > 0)
                  .map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "4px 8px" }}>{item.namaBarang}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {item.jumlahKeluar}
                      </td>
                      <td style={{ padding: "4px 8px" }}>{item.lot}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
